import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import Modal from "../Modal";
import { useUser } from "../../Data/UserContext";
import { getAuthHeaders, getVerifySessionUrl } from "../../utils/authHeaders";
import { normalizeSessionPayload } from "../../utils/sessionUser";
import { trackAvatarSelected } from "../../utils/frontendTrackingEvents";
import {
  getAvatarThumbSrc,
  type AvatarCatalogItem,
} from "../../utils/profileAvatarCatalog";

const AVATAR_MODAL_IMAGE_SIZES =
  "(max-width: 380px) 64px, (max-width: 640px) 72px, 80px";

const AVATAR_MODAL_CAROUSEL_VARS = {
  "--avatar-tile": "clamp(44px, 14vw, 80px)",
  "--avatar-gap": "8px",
  "--avatar-preview-tile": "clamp(72px, 22vw, 120px)",
} as React.CSSProperties;

const AVATAR_INITIAL_ACTIVE_SECTIONS = 2;
const AVATAR_SECTION_REVEAL_INTERVAL_MS = 180;
const FEATURE_UNLOCK_COST_RUBY = 1;
const FEATURE_UNLOCK_BONUS_COINS = 200;

const getAvatarThumbSrcSet = (avatar: AvatarCatalogItem) => {
  if (avatar?.thumb_srcset) return avatar.thumb_srcset;
  const entries = Object.entries(avatar?.thumb_urls || {})
    .map(([size, url]) => [Number.parseInt(size, 10), url] as const)
    .filter(([size, url]) => Number.isFinite(size) && size > 0 && Boolean(url))
    .sort((a, b) => a[0] - b[0]);
  const items = entries.map(([size, url]) => `${url} ${size}w`);
  return items.length > 0 ? items.join(", ") : undefined;
};

const AVATAR_KEY_ANIMAL_SLUGS = new Set([
  "croc",
  "giraffe",
  "owl",
  "shark",
  "elephant",
  "monkey",
  "rhino",
  "tiger",
]);

const getAvatarColorSortKey = (avatarKey: string) => {
  const k = String(avatarKey || "");
  const parts = k.split("-");
  if (parts.length < 2) return k;
  const slug = parts[0];
  if (!AVATAR_KEY_ANIMAL_SLUGS.has(slug)) return k;
  return parts.slice(1).join("-");
};

type AvatarFrameOption = {
  id: number;
  name: string;
  imageUrl: string;
  smallImageUrl: string | null;
  isAuto?: boolean;
  priceAmount?: number | null;
  priceCurrency?: "CR8_RBY" | "COINS" | null;
};

type AvatarFrameState = {
  frames: AvatarFrameOption[];
  /** Frames this player may buy; they move to `frames` once bought. */
  shopFrames: AvatarFrameOption[];
  activeFrameId: number | null;
};

const FRAME_CURRENCY_ICON = {
  CR8_RBY: "/assets/currency-ruby.png",
  COINS: "/assets/currency-coin.png",
} as const;
const FRAME_CURRENCY_NAME = { CR8_RBY: "rubies", COINS: "coins" } as const;

function parseFrameState(data: unknown): AvatarFrameState | null {
  const d = data as {
    frames?: unknown;
    shopFrames?: unknown;
    activeFrameId?: unknown;
  } | null;
  if (!d || !Array.isArray(d.frames)) return null;
  return {
    frames: d.frames as AvatarFrameOption[],
    shopFrames: Array.isArray(d.shopFrames)
      ? (d.shopFrames as AvatarFrameOption[])
      : [],
    activeFrameId: d.activeFrameId != null ? Number(d.activeFrameId) : null,
  };
}

type FrameSelection = number | "none";

export type AvatarChooserModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  avatarOptions: AvatarCatalogItem[];
  /** Optional: refresh parent (e.g. Account `thisAccount`) after save */
  onAfterSaveSuccess?: () => void | Promise<void>;
  /**
   * When opening from a preset tile, pre-select that key.
   * Omit or `undefined` to initialize from `user.avatar_key` (null = original photo).
   */
  initialSelectedKey?: string | null;
};

export function AvatarChooserModal({
  isOpen,
  onRequestClose,
  avatarOptions,
  onAfterSaveSuccess,
  initialSelectedKey,
}: AvatarChooserModalProps) {
  const { user, setUser } = useUser();
  const [selectedAvatarKey, setSelectedAvatarKey] = useState<string | null>(
    null
  );
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarFeedback, setAvatarFeedback] = useState("");
  const [avatarFeedbackError, setAvatarFeedbackError] = useState(false);
  const prefetchedAvatarUrlsRef = useRef(new Set<string>());
  const [loadedAvatarThumbKeys, setLoadedAvatarThumbKeys] = useState(
    () => new Set<string>()
  );
  const [activeAvatarSectionCount, setActiveAvatarSectionCount] = useState(
    AVATAR_INITIAL_ACTIVE_SECTIONS
  );
  const [openAvatarSections, setOpenAvatarSections] = useState<
    Record<string, boolean>
  >({});
  const [isProfilePreviewModalOpen, setIsProfilePreviewModalOpen] =
    useState(false);
  const [profilePreviewImageUrl, setProfilePreviewImageUrl] = useState("");
  const [frameState, setFrameState] = useState<AvatarFrameState | null>(null);
  const [pickedFrame, setPickedFrame] = useState<FrameSelection | null>(null);

  const prefetchAvatarUrls = useCallback((urls: string[] = []) => {
    for (const url of urls) {
      if (!url || prefetchedAvatarUrlsRef.current.has(url)) continue;
      prefetchedAvatarUrlsRef.current.add(url);
      const img = new Image();
      img.decoding = "async";
      img.src = url;
    }
  }, []);

  const avatarSections = useMemo(() => {
    const order = [
      "giraffe",
      "crocodile",
      "rhino",
      "tiger",
      "monkey",
      "owl",
      "shark",
      "elephant",
    ];
    const grouped = avatarOptions.reduce<Record<string, AvatarCatalogItem[]>>(
      (acc, option) => {
        const animal = option?.animal || "other";
        if (!acc[animal]) acc[animal] = [];
        acc[animal].push(option);
        return acc;
      },
      {}
    );
    return order
      .filter(
        (animal) => Array.isArray(grouped[animal]) && grouped[animal].length > 0
      )
      .map((animal) => ({
        animal,
        items: grouped[animal].slice().sort((a, b) => {
          const ca = getAvatarColorSortKey(a.key);
          const cb = getAvatarColorSortKey(b.key);
          const byColor = ca.localeCompare(cb, undefined, {
            sensitivity: "base",
          });
          if (byColor !== 0) return byColor;
          return String(a.key).localeCompare(String(b.key));
        }),
      }));
  }, [avatarOptions]);

  const hasOriginalPhoto = Boolean(
    user?.original_avatar && String(user.original_avatar).trim() !== ""
  );

  const selectedAvatarPreviewUrl = useMemo(() => {
    if (selectedAvatarKey == null) {
      return hasOriginalPhoto ? user?.original_avatar ?? null : null;
    }
    const selected = avatarOptions.find(
      (option) => option.key === selectedAvatarKey
    );
    return getAvatarThumbSrc(selected);
  }, [
    selectedAvatarKey,
    hasOriginalPhoto,
    user?.original_avatar,
    avatarOptions,
  ]);

  const selectedAvatarFullUrl = useMemo(() => {
    if (selectedAvatarKey == null) {
      return hasOriginalPhoto ? user?.original_avatar ?? null : null;
    }
    const selected = avatarOptions.find(
      (option) => option.key === selectedAvatarKey
    );
    return selected?.url || selected?.thumb_url || null;
  }, [
    selectedAvatarKey,
    hasOriginalPhoto,
    user?.original_avatar,
    avatarOptions,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    let next: string | null = null;
    if (user?.avatar_key != null && String(user.avatar_key).trim() !== "") {
      next = String(user.avatar_key).trim();
    }
    if (initialSelectedKey !== undefined) {
      next = initialSelectedKey;
    }
    setSelectedAvatarKey(next);
    setLoadedAvatarThumbKeys(new Set());
    setActiveAvatarSectionCount(AVATAR_INITIAL_ACTIVE_SECTIONS);
    setAvatarFeedback("");
    setAvatarFeedbackError(false);
    const urls = avatarOptions
      .map((a) => getAvatarThumbSrc(a))
      .filter(Boolean) as string[];
    prefetchAvatarUrls(urls.slice(0, 28));
    prefetchAvatarUrls(urls.slice(0, 56));
  }, [
    isOpen,
    user?.avatar_key,
    initialSelectedKey,
    avatarOptions,
    prefetchAvatarUrls,
  ]);

  useEffect(() => {
    setOpenAvatarSections((prev) => {
      const next = { ...prev };
      for (const section of avatarSections) {
        if (next[section.animal] == null) next[section.animal] = true;
      }
      return next;
    });
  }, [avatarSections]);

  useEffect(() => {
    if (!isOpen) return;
    prefetchAvatarUrls(
      [
        selectedAvatarFullUrl,
        selectedAvatarPreviewUrl,
        user?.original_avatar,
      ].filter(Boolean) as string[]
    );
    const visibleSectionUrls = avatarSections
      .slice(0, activeAvatarSectionCount)
      .flatMap((section) => section.items.map((a) => getAvatarThumbSrc(a)))
      .filter(Boolean) as string[];
    const warmRemaining = () => prefetchAvatarUrls(visibleSectionUrls);
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = window.requestIdleCallback(warmRemaining, { timeout: 1200 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(warmRemaining, 124);
    return () => clearTimeout(t);
  }, [
    isOpen,
    avatarSections,
    activeAvatarSectionCount,
    selectedAvatarFullUrl,
    selectedAvatarPreviewUrl,
    user?.original_avatar,
    prefetchAvatarUrls,
  ]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (activeAvatarSectionCount >= avatarSections.length) return undefined;
    const id = setInterval(() => {
      setActiveAvatarSectionCount((prev) => {
        if (prev >= avatarSections.length) return prev;
        return prev + 1;
      });
    }, AVATAR_SECTION_REVEAL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOpen, activeAvatarSectionCount, avatarSections.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setPickedFrame(null);
    fetch(`${process.env.API_URL}/account/avatar-frames`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const next = parseFrameState(data);
        if (!cancelled && next) setFrameState(next);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const savedFrame: FrameSelection = frameState?.activeFrameId ?? "none";
  const selectedFrame: FrameSelection = pickedFrame ?? savedFrame;
  const selectedShopFrame =
    selectedFrame === "none"
      ? null
      : frameState?.shopFrames.find((f) => f.id === selectedFrame) ?? null;
  const frameChanged =
    Boolean(frameState) && selectedFrame !== savedFrame && !selectedShopFrame;
  const selectedFrameUrl =
    selectedFrame === "none"
      ? null
      : (
          selectedShopFrame ??
          frameState?.frames.find((f) => f.id === selectedFrame)
        )?.imageUrl ?? null;
  const hasFrameOptions = Boolean(
    frameState &&
      (frameState.frames.length > 0 || frameState.shopFrames.length > 0)
  );
  const framePreviewAvatarUrl =
    selectedAvatarFullUrl || selectedAvatarPreviewUrl || user?.avatar || "";

  const allowEdit = user?.allow_player_name_edit === true;

  const refreshSessionUser = async () => {
    const verifyUrl = getVerifySessionUrl();
    if (!verifyUrl || !setUser) return;
    try {
      const userResponse = await fetch(verifyUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (userResponse.status === 200) {
        setUser(
          normalizeSessionPayload(
            (await userResponse.json()) as Record<string, unknown>
          )
        );
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production")
        console.error("Failed to refresh user context", e);
    }
  };

  /** @returns error message, or null when saved / nothing to save. */
  const saveFrameChoice = async (): Promise<string | null> => {
    if (!frameChanged) return null;
    const response = await fetch(
      `${process.env.API_URL}/account/avatar-frame`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ choice: selectedFrame }),
      }
    );
    if (response.ok) return null;
    const data = await response.json().catch(() => ({}));
    return data?.message || "Could not update frame.";
  };

  const handleSaveFrameOnly = async () => {
    setAvatarSaving(true);
    setAvatarFeedback("");
    setAvatarFeedbackError(false);
    try {
      const frameError = await saveFrameChoice();
      if (frameError) {
        setAvatarFeedback(frameError);
        setAvatarFeedbackError(true);
        return;
      }
      await refreshSessionUser();
      await onAfterSaveSuccess?.();
      onRequestClose();
    } catch {
      setAvatarFeedback("Something went wrong. Please try again.");
      setAvatarFeedbackError(true);
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleBuyFrame = async () => {
    if (!selectedShopFrame) return;
    setAvatarSaving(true);
    setAvatarFeedback("");
    setAvatarFeedbackError(false);
    try {
      const response = await fetch(
        `${process.env.API_URL}/account/avatar-frames/${selectedShopFrame.id}/buy`,
        { method: "POST", headers: getAuthHeaders() }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setAvatarFeedback(data?.message || "Could not buy this frame.");
        setAvatarFeedbackError(true);
        return;
      }
      const next = parseFrameState(data);
      if (next) setFrameState(next);
      setPickedFrame(null);
      window.dispatchEvent(new CustomEvent("walletUpdate"));
      await refreshSessionUser();
      await onAfterSaveSuccess?.();
      setAvatarFeedback(
        `${selectedShopFrame.name} is yours and now on your avatar.`
      );
    } catch {
      setAvatarFeedback("Something went wrong. Please try again.");
      setAvatarFeedbackError(true);
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleSaveAvatar = async () => {
    if (!allowEdit) {
      setAvatarFeedback(
        `Unlock feature pack to change avatar (${FEATURE_UNLOCK_COST_RUBY} ruby + ${FEATURE_UNLOCK_BONUS_COINS} coins).`
      );
      setAvatarFeedbackError(true);
      return;
    }
    setAvatarSaving(true);
    setAvatarFeedback("");
    setAvatarFeedbackError(false);
    try {
      const response = await fetch(
        `${process.env.API_URL}/account/update-avatar`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ avatar_key: selectedAvatarKey }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setAvatarFeedback("Avatar updated.");
        setAvatarFeedbackError(false);
        const avatarId =
          data?.avatar_key != null && String(data.avatar_key).trim() !== ""
            ? String(data.avatar_key).trim()
            : null;
        if (avatarId && user?.userId != null && user.userId !== "") {
          trackAvatarSelected({
            user_id: user.userId,
            avatar_id: avatarId,
          });
        }
        const frameError = await saveFrameChoice();
        await refreshSessionUser();
        if (frameError) {
          setAvatarFeedback(
            `Avatar updated, but the frame was not saved: ${frameError}`
          );
          setAvatarFeedbackError(true);
          return;
        }
        await onAfterSaveSuccess?.();
        onRequestClose();
      } else {
        setAvatarFeedback(data?.message || "Could not update avatar.");
        setAvatarFeedbackError(true);
      }
    } catch {
      setAvatarFeedback("Something went wrong. Please try again.");
      setAvatarFeedbackError(true);
    } finally {
      setAvatarSaving(false);
    }
  };

  const openShopDrawer = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("openShopDrawer"));
    }
  };

  return (
    <>
      <Modal
        isOpen={isProfilePreviewModalOpen}
        onRequestClose={() => setIsProfilePreviewModalOpen(false)}
        style={{
          overlay: {
            zIndex: 4000,
          },
          content: {
            zIndex: 4001,
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            width: "min(94vw, 520px)",
            background: "#1b062f",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            padding: "16px",
            textAlign: "center",
          },
        }}
        contentLabel="Profile picture preview"
        ariaHideApp={false}
      >
        <img
          src={profilePreviewImageUrl || user?.avatar || ""}
          className="mx-auto h-auto w-full max-w-[min(88vw,440px)] border border-white/15 object-contain"
          alt="Profile picture preview"
        />
      </Modal>

      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        overlayClassName="avatar-modal-choose-overlay"
        className="avatar-modal-choose box-border outline-none hide-scrollbar min-h-0 max-h-full w-full max-w-full overflow-x-hidden overflow-y-auto"
        style={{
          overlay: {
            display: "flex",
            alignItems: "stretch",
            justifyContent: "stretch",
            padding: 0,
            overflow: "hidden",
            touchAction: "auto",
          },
          content: {
            position: "relative",
            top: "auto",
            left: "auto",
            right: "auto",
            bottom: "auto",
            margin: "0",
            transform: "none",
            flex: "1 1 auto",
            alignSelf: "stretch",
            width: "100%",
            maxWidth: "100%",
            height: "100dvh",
            maxHeight: "100dvh",
            minHeight: "100dvh",
            background: "#1b062f",
            color: "#fff",
            border: "none",
            padding: "0",
            boxSizing: "border-box",
          },
        }}
        contentLabel="Choose Avatar"
        ariaHideApp={false}
      >
        <div
          className="avatar-modal-choose-body flex h-full min-h-0 max-h-full w-full min-w-0 max-w-full flex-1 flex-col overflow-x-hidden overflow-y-hidden bg-[radial-gradient(circle_at_top,rgba(210,255,0,0.12),rgba(27,6,47,0.92)_32%,rgba(27,6,47,1)_62%)] pt-[max(12px,env(safe-area-inset-top,0px))] pr-[max(12px,env(safe-area-inset-right,0px))] pb-[max(12px,env(safe-area-inset-bottom,0px))] pl-[max(12px,env(safe-area-inset-left,0px))] max-[380px]:pt-[max(8px,env(safe-area-inset-top,0px))] max-[380px]:pr-[max(8px,env(safe-area-inset-right,0px))] max-[380px]:pb-[max(8px,env(safe-area-inset-bottom,0px))] max-[380px]:pl-[max(8px,env(safe-area-inset-left,0px))] sm:pt-[max(16px,env(safe-area-inset-top,0px))] sm:pr-[max(16px,env(safe-area-inset-right,0px))] sm:pb-[max(16px,env(safe-area-inset-bottom,0px))] sm:pl-[max(16px,env(safe-area-inset-left,0px))]"
          style={AVATAR_MODAL_CAROUSEL_VARS}
        >
          <div className="mb-2 shrink-0 border border-white/10 bg-[#27183f]/85 px-3 py-2 shadow-[0_8px_22px_rgba(0,0,0,0.38)] sm:mb-[12px] sm:px-4 sm:py-2.5">
            <div className="font-anton text-[16px] leading-[1.2] text-white sm:text-[18px]">
              Choose Avatar
            </div>
            <div className="mt-0.5 font-chakra text-[11px] text-white/70 normal-case sm:text-[12px]">
              Pick a style that matches your vibe.
            </div>
          </div>
          {hasOriginalPhoto && (
            <div className="mb-2 shrink-0 border border-white/10 bg-white/[0.04] px-2 py-2 sm:mb-[10px] sm:px-2.5">
              <div className="mb-[6px] font-chakra text-[10px] text-white/80 normal-case">
                Use original photo
              </div>
              <button
                type="button"
                className={`inline-flex aspect-square h-[30px] w-[30px] min-w-0 shrink-0 items-center justify-center overflow-hidden rounded-none border-2 p-0 leading-none transition-all duration-150 ${
                  selectedAvatarKey == null
                    ? "border-[#d2ff00] shadow-[0_0_0_1px_rgba(210,255,0,0.22)]"
                    : "border-white/20 hover:border-white/45"
                }`}
                onClick={() => setSelectedAvatarKey(null)}
                aria-label="Use original profile photo"
              >
                <img
                  src={user?.original_avatar || ""}
                  className="block h-[30px] w-[30px] object-cover"
                  alt="Original avatar"
                  decoding="async"
                />
              </button>
            </div>
          )}
          {frameState && hasFrameOptions ? (
            <div className="mb-2 shrink-0 border border-[#d2ff00]/30 bg-[#2a1f43]/80 px-2 pb-2 pt-1.5 sm:mb-[12px] sm:px-2.5">
              <div className="mb-[6px] flex items-center gap-2">
                <span className="h-[1px] flex-1 bg-white/20" />
                <span className="font-anton text-[11px] uppercase tracking-[1.3px] text-[#d2ff00]">
                  Frame
                </span>
                <span className="h-[1px] flex-1 bg-white/20" />
              </div>
              <div className="hide-scrollbar flex flex-row flex-nowrap gap-[var(--avatar-gap)] overflow-x-auto py-[1px]">
                {[
                  {
                    value: "none" as FrameSelection,
                    label: "No frame",
                    src: null as string | null,
                    price: null as AvatarFrameOption | null,
                  },
                ]
                  .concat(
                    frameState.frames.map((f) => ({
                      value: f.id,
                      label: f.name,
                      src: f.imageUrl,
                      price: null,
                    }))
                  )
                  .concat(
                    frameState.shopFrames.map((f) => ({
                      value: f.id,
                      label: f.name,
                      src: f.imageUrl,
                      price: f,
                    }))
                  )
                  .map((option) => {
                    const active = selectedFrame === option.value;
                    return (
                      <button
                        key={String(option.value)}
                        type="button"
                        className="flex shrink-0 flex-col items-center gap-1 bg-transparent p-0"
                        onClick={() => setPickedFrame(option.value)}
                        aria-pressed={active}
                        aria-label={
                          option.price?.priceAmount != null &&
                          option.price.priceCurrency
                            ? `${option.label} frame, ${
                                option.price.priceAmount
                              } ${
                                FRAME_CURRENCY_NAME[option.price.priceCurrency]
                              }`
                            : option.src
                            ? `${option.label} frame`
                            : "No frame"
                        }
                      >
                        <span
                          className={`relative block overflow-hidden border-2 bg-[#2a2342] transition-all duration-150 ${
                            active
                              ? "border-[#d2ff00] shadow-[0_0_0_1px_rgba(210,255,0,0.28),0_6px_16px_rgba(0,0,0,0.35)]"
                              : "border-transparent hover:border-white/30"
                          }`}
                          style={{
                            width: "var(--avatar-tile)",
                            height: "var(--avatar-tile)",
                          }}
                        >
                          {framePreviewAvatarUrl ? (
                            <img
                              src={framePreviewAvatarUrl}
                              alt=""
                              className="block h-full w-full object-cover"
                              decoding="async"
                            />
                          ) : null}
                          {option.src ? (
                            <img
                              src={option.src}
                              alt=""
                              className="pointer-events-none absolute inset-0 block h-full w-full"
                              decoding="async"
                            />
                          ) : null}
                          {option.price?.priceAmount != null &&
                          option.price.priceCurrency ? (
                            <span className="absolute bottom-0 right-0 flex items-center gap-0.5 bg-[#1b062f]/90 px-1 py-[1px] font-anton text-[10px] leading-none text-white">
                              {option.price.priceAmount.toLocaleString()}
                              <img
                                src={
                                  FRAME_CURRENCY_ICON[
                                    option.price.priceCurrency
                                  ]
                                }
                                alt=""
                                className="h-3 w-3 object-contain"
                                aria-hidden
                              />
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`max-w-[var(--avatar-tile)] truncate font-chakra text-[10px] normal-case ${
                            active ? "text-[#d2ff00]" : "text-white/70"
                          }`}
                        >
                          {option.label}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          ) : null}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden hide-scrollbar pr-[2px] pt-0 max-[380px]:pr-1">
            {avatarSections.map((section, sectionIndex) => (
              <div
                key={section.animal}
                className="mb-2 border border-white/10 bg-[#2a1f43]/70 px-2 pb-2 pt-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] last:mb-0 sm:mb-[12px] max-[380px]:px-1.5 max-[380px]:pb-1.5 max-[380px]:pt-0"
              >
                <button
                  type="button"
                  className="mb-[8px] flex w-full items-center gap-2"
                  onClick={() =>
                    setOpenAvatarSections((prev) => ({
                      ...prev,
                      [section.animal]: prev[section.animal] === false,
                    }))
                  }
                  aria-label={`Toggle ${section.animal} avatars`}
                >
                  <span className="h-[1px] flex-1 bg-white/20" />
                  <span className="font-anton text-[11px] uppercase tracking-[1.3px] text-[#d2ff00]">
                    {section.animal}
                  </span>
                  <span className="h-[1px] flex-1 bg-white/20" />
                </button>
                {openAvatarSections[section.animal] !== false && (
                  <div className="relative mt-[4px]">
                    {(sectionIndex >= activeAvatarSectionCount ||
                      section.items.every(
                        (avatar) => !loadedAvatarThumbKeys.has(avatar.key)
                      )) && (
                      <div
                        className="pointer-events-none absolute inset-0 z-10 flex items-center gap-[var(--avatar-gap)] overflow-hidden"
                        aria-hidden
                      >
                        {section.items.map((avatar) => (
                          <span
                            key={`skeleton-${avatar.key}`}
                            className="relative aspect-square shrink-0 overflow-hidden border border-white/10 bg-[#231a38] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
                            style={{
                              width: "var(--avatar-tile)",
                              height: "var(--avatar-tile)",
                            }}
                          >
                            <span className="absolute inset-[20%] bg-white/8" />
                            <span className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/12 to-transparent" />
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex w-full min-w-0 justify-center overflow-hidden">
                      <div className="avatar-carousel-viewport hide-scrollbar max-w-full shrink-0 touch-pan-x snap-x snap-mandatory overflow-x-auto overflow-y-hidden">
                        <div
                          className="flex w-max flex-row flex-nowrap items-center gap-[var(--avatar-gap)] py-[1px]"
                          style={{ minHeight: "var(--avatar-tile)" }}
                        >
                          {section.items.map((avatar) => (
                            <button
                              key={avatar.key}
                              type="button"
                              className={`flex aspect-square shrink-0 snap-start items-center justify-center overflow-hidden rounded-none border-2 bg-[#2a2342] p-0 transition-all duration-150 hover:-translate-y-[1px] hover:scale-[1.02] ${
                                selectedAvatarKey === avatar.key
                                  ? "border-[#d2ff00] shadow-[0_0_0_1px_rgba(210,255,0,0.28),0_6px_16px_rgba(0,0,0,0.35)]"
                                  : "border-transparent hover:border-white/30"
                              }`}
                              onClick={() => setSelectedAvatarKey(avatar.key)}
                              aria-label={`${avatar.animal} avatar`}
                              disabled={
                                sectionIndex >= activeAvatarSectionCount
                              }
                              style={{
                                width: "var(--avatar-tile)",
                                height: "var(--avatar-tile)",
                              }}
                            >
                              <img
                                src={
                                  sectionIndex < activeAvatarSectionCount
                                    ? getAvatarThumbSrc(avatar)
                                    : undefined
                                }
                                srcSet={
                                  sectionIndex < activeAvatarSectionCount
                                    ? getAvatarThumbSrcSet(avatar)
                                    : undefined
                                }
                                sizes={AVATAR_MODAL_IMAGE_SIZES}
                                className={`block h-full w-full object-cover transition-opacity duration-150 ${
                                  sectionIndex < activeAvatarSectionCount &&
                                  loadedAvatarThumbKeys.has(avatar.key)
                                    ? "opacity-100"
                                    : "opacity-70"
                                }`}
                                alt={`${avatar.animal} avatar`}
                                loading="lazy"
                                {...({ fetchpriority: "low" } as any)}
                                decoding="async"
                                onLoad={() => {
                                  setLoadedAvatarThumbKeys((prev) => {
                                    if (prev.has(avatar.key)) return prev;
                                    const next = new Set(prev);
                                    next.add(avatar.key);
                                    return next;
                                  });
                                }}
                                onError={() => {
                                  setLoadedAvatarThumbKeys((prev) => {
                                    if (prev.has(avatar.key)) return prev;
                                    const next = new Set(prev);
                                    next.add(avatar.key);
                                    return next;
                                  });
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 flex shrink-0 flex-row flex-nowrap items-center justify-between gap-2 border border-white/10 bg-[#241538]/90 px-2.5 py-2.5 shadow-[0_-8px_24px_rgba(0,0,0,0.3)] sm:mt-[14px] sm:gap-3 sm:px-3 sm:py-3">
            <div className="flex shrink-0 items-center justify-start">
              <div className="relative">
                <button
                  type="button"
                  className={`relative flex aspect-square shrink-0 items-center justify-center overflow-hidden rounded-none border border-white/30 bg-[#2a2342] p-0 transition-colors duration-150 ${
                    allowEdit ? "hover:border-white/50" : "cursor-not-allowed"
                  }`}
                  style={{
                    width: "var(--avatar-preview-tile)",
                    height: "var(--avatar-preview-tile)",
                  }}
                  onClick={() => {
                    if (!allowEdit) return;
                    if (!selectedAvatarFullUrl) return;
                    setProfilePreviewImageUrl(selectedAvatarFullUrl);
                    setIsProfilePreviewModalOpen(true);
                  }}
                  aria-label={
                    allowEdit
                      ? "Open selected avatar preview"
                      : "Avatar preview locked"
                  }
                  disabled={!allowEdit}
                >
                  <span className="absolute inset-0 overflow-hidden">
                    {selectedAvatarPreviewUrl ? (
                      <img
                        src={selectedAvatarFullUrl || selectedAvatarPreviewUrl}
                        className={`absolute inset-0 m-0 block h-full w-full max-w-none object-cover p-0 ${
                          !allowEdit ? "opacity-60" : ""
                        }`}
                        alt="Selected avatar preview"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center font-chakra text-xs text-white/60">
                        none
                      </span>
                    )}
                    {selectedFrameUrl ? (
                      <img
                        src={selectedFrameUrl}
                        alt=""
                        className="pointer-events-none absolute inset-0 m-0 block h-full w-full max-w-none p-0"
                        decoding="async"
                        aria-hidden
                      />
                    ) : null}
                  </span>
                </button>
                {!allowEdit && (
                  <span
                    className="pointer-events-none absolute inset-0 flex items-center justify-center"
                    aria-hidden
                  >
                    <Icon
                      icon="mdi:lock"
                      className="h-7 w-7 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] sm:h-8 sm:w-8"
                    />
                  </span>
                )}
              </div>
            </div>
            <div className="flex min-w-0 flex-1 flex-row flex-nowrap items-center justify-end gap-1.5 sm:gap-3">
              {allowEdit ? (
                <button
                  type="button"
                  className="shrink-0 rounded-none bg-[#d2ff00] px-3 py-2 font-anton text-[12px] tracking-[0.3px] text-[#1b062f] shadow-[0_3px_10px_rgba(210,255,0,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:px-3.5 sm:py-2.5 sm:text-[14px] md:p-[10px] md:text-[16px]"
                  onClick={() => void handleSaveAvatar()}
                  disabled={avatarSaving}
                >
                  {avatarSaving ? "Saving..." : "Save Avatar"}
                </button>
              ) : (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center justify-center gap-1 rounded-none bg-[#d2ff00] px-3 py-2 font-anton text-[11px] tracking-[0.3px] text-[#1b062f] shadow-[0_3px_10px_rgba(210,255,0,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:gap-1.5 sm:px-3 sm:py-2.5 sm:text-[13px] md:p-[10px] md:text-[16px]"
                  onClick={openShopDrawer}
                >
                  Unlock for {FEATURE_UNLOCK_COST_RUBY}
                  <img
                    src="/assets/currency-ruby.png"
                    alt=""
                    className="inline-block h-4 w-4 object-contain"
                    aria-hidden
                  />
                </button>
              )}
              {selectedShopFrame?.priceAmount != null &&
              selectedShopFrame.priceCurrency ? (
                <button
                  type="button"
                  className="inline-flex shrink-0 items-center justify-center gap-1 rounded-none bg-[#d2ff00] px-3 py-2 font-anton text-[12px] tracking-[0.3px] text-[#1b062f] shadow-[0_3px_10px_rgba(210,255,0,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:px-3.5 sm:py-2.5 sm:text-[14px] md:p-[10px] md:text-[16px]"
                  onClick={() => void handleBuyFrame()}
                  disabled={avatarSaving}
                >
                  {avatarSaving ? (
                    "Buying..."
                  ) : (
                    <>
                      Buy frame for{" "}
                      {selectedShopFrame.priceAmount.toLocaleString()}
                      <img
                        src={
                          FRAME_CURRENCY_ICON[selectedShopFrame.priceCurrency]
                        }
                        alt=""
                        className="inline-block h-4 w-4 object-contain"
                        aria-hidden
                      />
                    </>
                  )}
                </button>
              ) : null}
              {!allowEdit && frameChanged ? (
                <button
                  type="button"
                  className="shrink-0 rounded-none bg-[#d2ff00] px-3 py-2 font-anton text-[12px] tracking-[0.3px] text-[#1b062f] shadow-[0_3px_10px_rgba(210,255,0,0.3)] disabled:cursor-not-allowed disabled:opacity-60 sm:px-3.5 sm:py-2.5 sm:text-[14px] md:p-[10px] md:text-[16px]"
                  onClick={() => void handleSaveFrameOnly()}
                  disabled={avatarSaving}
                >
                  {avatarSaving ? "Saving..." : "Save Frame"}
                </button>
              ) : null}
              <button
                type="button"
                className="shrink-0 rounded-none border border-white/20 bg-[#3a2952] px-3 py-2 font-anton text-[12px] text-white transition-colors duration-150 hover:border-white/40 sm:px-3 sm:py-2.5 sm:text-[14px] md:p-[10px] md:text-[16px]"
                onClick={onRequestClose}
              >
                Cancel
              </button>
            </div>
          </div>
          {avatarFeedback ? (
            <p
              className={`mt-2 shrink-0 px-2 text-center font-chakra text-[11px] normal-case sm:text-[12px] ${
                avatarFeedbackError ? "text-[#f87171]" : "text-[#32F78D]"
              }`}
            >
              {avatarFeedback}
            </p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
